import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { isAfter } from "date-fns";
import { PageTitle } from "@/react/prop-types";
import { DEFAULT_END_TIME, DEFAULT_START_TIME } from "@/react/shared/constants";
import type { GetAssignmentCreationSupportDataResponse } from "@laborchart-modules/lc-core-api/dist/api/legacy/get-assignment-creation-support-data";
import type { ProjectOptions } from "./types";
import {
   calculateTbdDate,
   getTimeOptionsFromProject,
   isEndDateInPast,
   updateProjectEndDate,
   updateStartDateIfFuture,
} from "./helper";

interface UseProjectDateTimeDefaultsParams {
   /** Derived from the selected project's metadata (via useDropDownSelectionHandler). */
   projectStartDate: Date | null;
   /** Derived from the selected project's metadata (via useDropDownSelectionHandler). */
   projectEndDate: Date | null;
   projectId: string | undefined;
   isCreateFlow: boolean;
   setInitialValues: Dispatch<SetStateAction<any>>;
   supportData: GetAssignmentCreationSupportDataResponse | null;
   /** Date from the calling context (e.g. Gantt drag-create, boards drop). Skips start date pre-population when set. */
   parentStartDate?: Date;
   /** Date from the calling context (e.g. Gantt drag-create, boards drop). Skips end date pre-population when set. */
   parentEndDate?: Date;
   resetStartDateOnProjectChange?: boolean;
}

/**
 * Pre-populates start/end dates and times from project defaults in create flow.
 * Used by both assignment and request tearsheets.
 *
 * - `parentStartDate` / `parentEndDate`: When provided (e.g. from Gantt drag-create),
 *   the respective date pre-population is skipped.
 * - `resetStartDateOnProjectChange`: When true, resets start date to today on
 *   every project change before conditionally setting to project start date.
 */
export function useProjectDateTimeDefaults({
   projectStartDate,
   projectEndDate,
   projectId,
   isCreateFlow,
   setInitialValues,
   supportData,
   parentStartDate,
   parentEndDate,
   resetStartDateOnProjectChange = false,
}: UseProjectDateTimeDefaultsParams) {
   // End date pre-population: uses the project's est_end_date when it is valid and in the future.
   // Falls back to a TBD-calculated date (company_tbd_weeks from an effective start) when the
   // project end date is missing, in the past, or before the parent-provided start date.
   useEffect(() => {
      if (!isCreateFlow || parentEndDate) return;

      const isEndDateBeforeStart =
         parentStartDate && projectEndDate && isAfter(parentStartDate, projectEndDate);

      if (projectEndDate && !isEndDateInPast(projectEndDate) && !isEndDateBeforeStart) {
         updateProjectEndDate(projectEndDate, isCreateFlow, setInitialValues);
      } else if ((projectStartDate || projectEndDate) && supportData?.data?.company_tbd_weeks) {
         // Prefer parentStartDate (boards/Gantt scenario), then future projectStartDate, then today
         const effectiveStartDate =
            parentStartDate ||
            (projectStartDate && isAfter(projectStartDate, new Date())
               ? projectStartDate
               : new Date());
         const tbdEndDate = calculateTbdDate(
            effectiveStartDate,
            supportData.data.company_tbd_weeks,
         );
         setInitialValues((prevValues: any) => ({
            ...prevValues,
            end_date: tbdEndDate,
         }));
      }
   }, [
      projectEndDate,
      projectStartDate,
      isCreateFlow,
      parentStartDate,
      parentEndDate,
      supportData?.data?.company_tbd_weeks,
   ]);

   // Start date pre-population: sets start_date to the project's start date when it is in the future.
   // When resetStartDateOnProjectChange is true (request tearsheet), resets to today first on every
   // project change, then conditionally overrides with the project start date if it is in the future.
   useEffect(() => {
      if (!isCreateFlow || parentStartDate) return;
      updateStartDateIfFuture(
         projectStartDate,
         isCreateFlow,
         setInitialValues,
         resetStartDateOnProjectChange ? PageTitle.SCHEDULE_CARD : undefined,
      );
   }, [projectStartDate, isCreateFlow, parentStartDate]);

   // Time pre-population: sets start_time/end_time from the project's daily_start_time and
   // daily_end_time. Depends on async supportData (project times aren't available at mount),
   // so this cannot be moved into formInitialValues.
   useEffect(() => {
      if (!isCreateFlow || !projectId || !supportData?.data) return;

      const selectedProject = supportData.data.project_options.find(
         (project) => project.id === projectId,
      ) as ProjectOptions | undefined;

      if (!selectedProject) return;

      const projectStartTime = getTimeOptionsFromProject(
         selectedProject,
         DEFAULT_START_TIME,
         "daily_start_time",
      );
      const projectEndTime = getTimeOptionsFromProject(
         selectedProject,
         DEFAULT_END_TIME,
         "daily_end_time",
      );

      setInitialValues((prevValues: any) => ({
         ...prevValues,
         ...(projectStartTime?.label && {
            start_time: projectStartTime as typeof prevValues.start_time,
         }),
         ...(projectEndTime?.label && { end_time: projectEndTime as typeof prevValues.end_time }),
      }));
   }, [projectId, isCreateFlow, supportData?.data]);
}
